import { type Component, For, Show } from "solid-js";

import {
  SEARCH_PROVIDERS,
  type SearchProviderId,
} from "../searchProviders";
import {
  SLASH_MENU_ITEMS,
  type SlashItemId,
  type SlashMenuStage,
} from "./slashMenuModel";

interface SlashMenuProps {
  open: boolean;
  /** `items` is Image/Search/Agent/Attach; `providers` is the Search submenu. */
  stage: SlashMenuStage;
  onSelect: (id: SlashItemId) => void;
  onSelectProvider: (id: SearchProviderId) => void;
  onBack: () => void;
  /** Currently armed provider, so the submenu can mark it. */
  armed: SearchProviderId | null;
}

// Modality items in locked order. Agent is the fourth because it acts
// on a live page — a different modality from Search (read).
// Visual: 320px popover, 0.5px border, 10px radius, opens above the
// input. Animation: 0.12s ease-out menuIn (declared in styles.css).
const SlashMenu: Component<SlashMenuProps> = (props) => (
  <Show when={props.open}>
    <div class="neuro-slash-menu" data-neuro-slash-menu>
      <Show
        when={props.stage === "providers"}
        fallback={
          <For each={SLASH_MENU_ITEMS}>
            {(item) => (
              <button
                type="button"
                class="neuro-slash-item"
                onClick={() => props.onSelect(item.id)}
              >
                <span class="neuro-slash-icon">
                  <SlashIcon id={item.id} />
                </span>
                <span class="neuro-slash-text">
                  <span class="neuro-slash-label">{item.label}</span>
                  <span class="neuro-slash-desc">{item.description}</span>
                </span>
                <span class="neuro-slash-shortcut">{item.command}</span>
              </button>
            )}
          </For>
        }
      >
        {/* Search submenu. TinyFish Search+Fetch lives here, not as a
            fifth item. Agent is a first-stage modality. */}
        <button
          type="button"
          class="neuro-slash-item"
          onClick={() => props.onBack()}
        >
          <span class="neuro-slash-icon" aria-hidden="true">
            ←
          </span>
          <span class="neuro-slash-text">
            <span class="neuro-slash-label">Search with…</span>
          </span>
        </button>
        <For each={SEARCH_PROVIDERS}>
          {(p) => (
            <button
              type="button"
              class="neuro-slash-item"
              aria-pressed={props.armed === p.id}
              onClick={() => props.onSelectProvider(p.id)}
            >
              <span class="neuro-slash-icon">
                <SlashIcon id="search" />
              </span>
              <span class="neuro-slash-text">
                <span class="neuro-slash-label">{p.label}</span>
                <span class="neuro-slash-desc">{p.description}</span>
              </span>
              <span class="neuro-slash-shortcut">
                {props.armed === p.id ? "armed" : ""}
              </span>
            </button>
          )}
        </For>
      </Show>
    </div>
  </Show>
);

const SlashIcon: Component<{ id: SlashItemId }> = (props) => {
  // Inline SVG, no icon-font dep. Per handoff §6: every icon must be
  // intentional and individually justified — no react-icons.
  if (props.id === "image") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    );
  }
  if (props.id === "browse") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 010 20" />
        <path d="M12 2a15.3 15.3 0 000 20" />
      </svg>
    );
  }
  if (props.id === "search") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
};

export default SlashMenu;
